import { API } from "aws-amplify";

import { SimplApiHttpError } from "../../lib/simpl-api-client";

export type VerifyInviteResponse = {
  email: string;
  maskedEmail: string;
  redeemToken: string;
  expiresInSeconds: number;
  success?: boolean;
};

export type InviteApiErrorDetails = {
  maskedEmail?: string;
  [key: string]: unknown;
};

export class InviteApiError extends Error {
  status: number;
  details?: InviteApiErrorDetails;

  constructor(message: string, status: number, details?: InviteApiErrorDetails) {
    super(message);
    this.status = status;
    this.details = details;
    this.name = "InviteApiError";
  }
}

/**
 * SE-2314: registration answers 403 `sso_required` when the invitee's program
 * signs in through SSO and has no SIMPL password. The page then goes to that
 * sign-in instead of showing a password form. Returns the URL, or null.
 */
export const ssoSignInUrlFrom = (error: unknown): string | null => {
  if (!(error instanceof InviteApiError) || error.status !== 403) return null;
  const details = error.details;
  if (details?.code !== "sso_required") return null;
  const url = details.ssoSignInUrl;
  return typeof url === "string" && /^https?:\/\//.test(url) ? url : null;
};

type InviteRequestType = "verify" | "completeInvite" | "completeOnboarding";

const defaultErrors: Record<InviteRequestType, string> = {
  verify: "Unable to verify invitation link.",
  completeInvite: "Unable to complete invitation.",
  completeOnboarding: "Unable to record EULA acceptance."
};

type AmplifyApiError = {
  response?: {
    status?: number;
    statusCode?: number;
    data?: InviteApiErrorDetails & {
      error?: string;
      message?: string;
    };
  };
};

const buildInviteApiError = (
  error: unknown,
  fallback: string
): InviteApiError => {
  // `API.post` is routed through `simplApiRequest` (lib/api-interceptor),
  // which throws `SimplApiHttpError` with the parsed body in `response`.
  // Before this, every registration error read as a generic 500 here, so the
  // 409/410 screens and the SSO redirect (SE-2314) never fired.
  if (error instanceof SimplApiHttpError) {
    const body =
      error.response && typeof error.response === "object"
        ? (error.response as InviteApiErrorDetails & { error?: string; message?: string })
        : undefined;
    const message =
      (typeof body?.error === "string" && body.error) ||
      (typeof body?.message === "string" && body.message);
    return new InviteApiError(message || fallback, error.status, body);
  }

  const apiError = error as AmplifyApiError;
  const status =
    apiError?.response?.status ??
    apiError?.response?.statusCode ??
    500;

  const payload = apiError?.response?.data;
  const messageFromPayload =
    (typeof payload?.error === "string" && payload.error) ||
    (typeof payload?.message === "string" && payload.message);

  return new InviteApiError(
    messageFromPayload || fallback,
    status,
    payload
  );
};

async function postInviteRequest<TResponse>(
  path: string,
  body: Record<string, unknown>,
  type: InviteRequestType
): Promise<TResponse> {
  try {
    const response = await API.post("simpl-api", path, { body });
    return response as TResponse;
  } catch (error: unknown) {
    throw buildInviteApiError(error, defaultErrors[type]);
  }
}

export const verifyInviteRequest = (
  token: string
): Promise<VerifyInviteResponse> =>
  postInviteRequest("invites/verify", { token }, "verify");

export const completeInviteRequest = (input: {
  redeemToken: string;
  password: string;
}): Promise<{ maskedEmail?: string }> =>
  postInviteRequest("invites/complete", input, "completeInvite");

export const completeOnboardingRequest = async (): Promise<{
  eulaAcceptedDate: string;
  autoAssignedAssessments: number;
}> => {
  // Use API.post directly without body since this endpoint doesn't need any body data
  // Authentication is handled by simpl-api-client (Authorization: Bearer + cookie)
  try {
    const response = await API.post("simpl-api", "complete-onboarding", {});
    return response as {
      eulaAcceptedDate: string;
      autoAssignedAssessments: number;
    };
  } catch (error: unknown) {
    throw buildInviteApiError(error, defaultErrors.completeOnboarding);
  }
};
